import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { IMCMapView } from '../../../classes/mcMapView';
import { ESRI } from '../../../esri-loader-util';
import { ExportService } from '../../../services/export.service';
import { LayerService } from '../../../services/layer.service';
import { PrintService } from '../../../services/print.service';
import { IDefaultPrintTemplate, IExportParameters, ILayerTableChild, IMapcoreProps, IQuery, MapCoreLayer } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-export-map',
    templateUrl: './export-map.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './export-map.component.scss']
})
export class ExportMapComponent extends BaseComponent implements AfterViewInit {
    constructor(_vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        private readonly printService: PrintService,
        private readonly exportService: ExportService,
        private readonly cdr: ChangeDetectorRef) {
        super(_vcr, true);
    }
    @Input() mapViewRef: IMCMapView;
    _active: any;
    @Input()
    get active() {
        return this._active;
    }
    set active(value: any) {
        if (value) {
            this.onActivate();
        }
        this._active = value;
    }
    @Output() readonly onclose: EventEmitter<boolean> = new EventEmitter<boolean>();
    mapLayers: IExportableLayer[] = [];
    success = false;
    fileName: string;
    limitToMapExtent = false;
    includeMapImage = true;
    isLoading = false;

    /**
     * The Map Selected criteria to the exported map
     */
    @Input() mapSelect: string;

    statewideDefault = {
        xmin: -8963167.0999474,
        ymin: 4815933.74691319,
        xmax: -8306549.08350328,
        ymax: 5238680.50536529
    };
    private _mode: string = 'excel';
    @Input()
    get mode() {
        return this._mode.toLowerCase();
    }
    set mode(value: string) {
        this._mode = value.toLowerCase();
        //TODO: add in code to re-initialize data for the dialouge
    }

    get allChecked() {
        return this.mapLayers.every(layer => layer.isChecked && layer.childTables.every(child => child.isChecked));
    }
    set allChecked(value: boolean) {
        for (const layer of this.mapLayers) {
            layer.isChecked = value;
            for (const child of layer.childTables) {
                child.isChecked = value;
            }
        }
        this.cdr.detectChanges();
    }
    get someChecked() {
        return !this.allChecked && this.mapLayers.some(layer => layer.isChecked || layer.childTables.some(child => child.isChecked));
    }

    async ngAfterViewInit() {
        this.mapViewRef = this.mapViewRef ? this.mapViewRef : await this.setupMapViewRef(this.mapViewRef);
        this.isReady();
    }

    async onActivate() {
        this.isLoading = true;
        this.fileName = this.mapViewRef.mapcoreViewProps.name;
        this.mapLayers = this.layerService.mapRef.allLayers
            .filter((layer: MapCoreLayer) => layer.type === 'map-image' && layer.mapcoreProps && !layer.mapcoreProps.hideInLegend)
            .map((layer: MapCoreLayer) => ({
                esriLayer: layer,
                mapcoreProps: layer.mapcoreProps,
                childTables: [],
                isChecked: false
            } as IExportableLayer)).toArray().reverse();
        const childTablePromises: Promise<void>[] = [];
        for (const layer of this.mapLayers) {
            childTablePromises.push(this.layerService.getLayerChildTables(this.getLayerId(layer.mapcoreProps))
                .then(childTables => {
                    if (childTables && childTables.length) {
                        layer.childTables = childTables.map(t => ({ ...t, isChecked: false } as IExportableLayerTableChild));
                    }
                }));
        }
        await Promise.all(childTablePromises);
        this.isLoading = false;
        this.cdr.detectChanges();
    }

    async exportShapefile() {
            return Promise.all(this.mapLayers.filter(x => x.isChecked).map(async layer => {
                const props = layer.mapcoreProps;
                const rootQuery: IQuery = { operator: 'AND', subqueries: [] };
                if (props.parentQuery) {
                    rootQuery.subqueries.push(props.parentQuery);
                }

                if (props.query) {
                    rootQuery.subqueries.push(props.query);
                }

                let geometries = null;
                if (layer && layer.esriLayer.type && layer.esriLayer.type.toLowerCase() === 'graphics') {
                    geometries = await this.layerService.getLayerGeometrySingleType(layer.esriLayer);
                }

                const exportShapeFileParams = {
                    name: props.name,
                    columns: props.columns,
                    tables: props.tables,
                    query: rootQuery,
                    geometries: geometries ? geometries.map(x => x.toJSON()) : null
                };

                return this.exportService.exportToShapeFileAndOpenUrl(exportShapeFileParams);
            }));
    }

    async exportExcel() {
        for (const layer of this.mapLayers) {
            layer.mapcoreProps.showInPrintLegend = layer.isChecked;
        }

        const layers = this.mapLayers.filter(x => x.isChecked).map(y => {
            const rootQuery: IQuery = this.layerService.modifyQueryForExport(y.mapcoreProps.parentQuery, y.mapcoreProps.query);

            return ({
                id: this.getLayerId(y.mapcoreProps),
                query: rootQuery,
                name: y.mapcoreProps.name,
                columns: y.mapcoreProps.columns
            } as IMapcoreProps);
        });

        // contains db order of column names, used in API through exportParams
        const fieldNameOrder = [];
        layers.forEach(layer => {
            let layerColumns = layer.columns;
            if (layerColumns) {
                layerColumns = layerColumns.sort(
                    (a, b) => {
                        if (a.databaseColumnOrder > b.databaseColumnOrder) {
                            return 1;
                        } else if (
                            a.databaseColumnOrder < b.databaseColumnOrder
                        ) {
                            return -1;
                        }
                        return 0;
                    }
                );
                fieldNameOrder.push(layerColumns.map(x => x.name.toUpperCase()));
            }
        });
        const childTableIds = this.mapLayers.map(l => l.childTables).flat().filter(ct => ct.isChecked).map(ct => ct.tableId);
        const exportParams: IExportParameters = { layers, childTableIds, includeLayerChildren: !!childTableIds.length, fieldNameOrder };
        if (this.includeMapImage) {
            const printUrl = await this.printMap();
            exportParams.imageUrl = printUrl;
        }
        await this.exportService.exportToExcelAndOpenUrl(exportParams);
    }

    async exportMap() {
        this.isLoading = true;
        if (this.mode === 'shapefile') {
            await this.exportShapefile();
        } else {
            this.exportExcel();
        }
        this.isLoading = false;
        this.cdr.detectChanges();
        this.close();
        }

    async printMap(): Promise<string> {
        if (!this.mapSelect) {
            this.mapSelect = '';
        }
        const template: IDefaultPrintTemplate = {
            selectedLayout: {
                name: 'ANSI A Landscape (11x8.5)',
                heightInches: 6.0953,
                widthInches: 9.7527,
                id: 1
            },
            label: '',
            format: 'png' as any,
            layoutOptions: {
                titleText: this.fileName,
                authorText: '',
                copyrightText: 'PennDOT',
                scalebarUnit: 'Miles',
                customTextElements: [{ selectText: this.mapSelect }],
                legendLayers: null
            },
            exportOptions: {
                dpi: 300
            }
        };

        try {
            let extent = this.mapViewRef.extent.clone();
            if (!this.limitToMapExtent) {
                const Extent = await ESRI.Extent;
                extent = new Extent({
                    xmin: this.statewideDefault.xmin,
                    xmax: this.statewideDefault.xmax,
                    ymin: this.statewideDefault.ymin,
                    ymax: this.statewideDefault.ymax,
                    spatialReference: this.mapViewRef.spatialReference.toJSON()
                });
            }
            return await this.printService.printMap(
                this.mapViewRef,
                template,
                extent,
                true,
                null,
                false, //TODO: Scale drawing layers is an app config option in 1.x. Set to false by default, should we enable a choice?
                true
            ).then(url => {
                return url;
            });
        } catch (error) {
            this.isLoading = false;
        }
    }

    resetSuccess() {
        this.success = false;
    }

    async close() {
        // reset file name. Layer selection gets reset on activate
        this.fileName = '';
        this.limitToMapExtent = false;
        this.mapLayers = [];
        this.resetSuccess();
        this.onclose.emit(true);
    }

    private getLayerId(mapcoreProps: IMapcoreProps) {
        return mapcoreProps.parentId || mapcoreProps.id || mapcoreProps.idFromSavedLayer;
    }

    toggleCheckAll() {
        this.allChecked = !this.allChecked;
    }

    checkedChange(layer: IExportableLayer) {
        layer.isChecked = !layer.isChecked;
        this.cdr.detectChanges();
    }

    childCheckedChange(child: IExportableLayerTableChild) {
        child.isChecked = !child.isChecked;
        this.cdr.detectChanges();
    }
}

interface IExportableLayer {
    esriLayer: MapCoreLayer;
    isChecked: boolean;
    mapcoreProps: IMapcoreProps;
    childTables: IExportableLayerTableChild[];
}

interface IExportableLayerTableChild extends ILayerTableChild {
    isChecked: boolean;
}
